import { useState, useEffect, useCallback } from 'react';
import { Gift, Plus, Trash2, Pencil, Loader2, Folder } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import ComboSkupinaEditor from './ComboSkupinaEditor';
import { api } from '../../lib/spizirnaUi';

const BARVA = '#22C55E';

/**
 * Přehled combo skupin — obdoba „Spravovat skupiny" u ingrediencí.
 *
 * Kde se combo nabízí, se nastavuje na kategorii nebo produktu (záložka
 * Combo); tady je jen seznam skupin, kolik mají položek a na kolika
 * místech jsou přiřazené. Úprava jde do ComboSkupinaEditor.
 */
export default function ComboSkupinySprava({ isDark, onZavrit }) {
    const [skupiny, setSkupiny] = useState(null);
    const [editor, setEditor] = useState(undefined); // undefined = zavřený, null = nová, id = úprava

    const muted = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const nacist = useCallback(async () => {
        try { setSkupiny(await api('/api/recepty/combo-skupiny')); } catch { setSkupiny([]); }
    }, []);
    useEffect(() => { nacist(); }, [nacist]);

    const smazat = async (s) => {
        if (!confirm(`Smazat combo „${s.nazev}"? Zmizí i jeho položky a ceny.`)) return;
        try { await api(`/api/recepty/combo-skupiny/${s.id}`, 'DELETE'); nacist(); }
        catch (e) { alert(e?.error || 'Combo se nepodařilo smazat'); }
    };

    return (
        <>
            <ModalShell onClose={onZavrit} isDark={isDark} ikona={Gift} barva={BARVA} sirka={560} nadpis="Combo skupiny"
                zavritLze={editor === undefined}>
                <div className="p-3 space-y-2">
                    <p className="text-[11px] px-1" style={{ color: muted }}>
                        Co combo nabízí a za kolik. Kde se nabízí, nastavíš v záložce Combo u kategorie nebo produktu.
                    </p>
                    <button type="button" onClick={() => setEditor(null)}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold"
                        style={{ background: `${BARVA}14`, color: BARVA, border: `1px dashed ${BARVA}66`, cursor: 'pointer' }}>
                        <Plus size={14} /> Nová combo skupina
                    </button>

                    {skupiny === null ? (
                        <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin" style={{ color: muted }} /></div>
                    ) : skupiny.length === 0 ? (
                        <p className="text-xs text-center py-6" style={{ color: muted }}>Zatím žádné combo.</p>
                    ) : skupiny.map(s => (
                        <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                            style={{ border: `1px solid ${border}`, opacity: s.aktivni ? 1 : 0.55 }}>
                            <button type="button" onClick={() => setEditor(s.id)} className="flex-1 min-w-0 text-left"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                <span className="block text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                    {s.nazev}
                                    {!s.aktivni && <span className="ml-2 text-[10px] font-medium" style={{ color: '#F59E0B' }}>vypnuté</span>}
                                </span>
                                <span className="block text-[11px]" style={{ color: muted }}>
                                    {s.typ_zobrazeni === 'auto' ? 'auto' : 'nabídka'} · {s.polozky_count || 0} položek
                                    {s.produktu_count ? ` · nabízí se u ${s.produktu_count} ${s.produktu_count === 1 ? 'produktu' : 'produktů'}` : ''}
                                </span>
                                <span className="block text-[11px] mt-0.5 truncate" style={{ color: 'var(--text-secondary)' }}>
                                    {(s.mista || []).filter(m => m.prirazena).length
                                        ? (s.mista || []).filter(m => m.prirazena).map((m, i) => (
                                            <span key={`${m.typ}:${m.id}`}>
                                                {i > 0 && ' · '}
                                                {m.typ === 'kategorie' && <Folder size={10} style={{ display: 'inline', marginRight: 3, verticalAlign: '-1px', color: BARVA }} />}
                                                {m.nazev}
                                            </span>
                                        ))
                                        : <span style={{ color: '#F59E0B' }}>nikde nepřiřazené</span>}
                                    {(s.mista || []).some(m => !m.prirazena) && (
                                        <span style={{ color: '#EF4444' }}> · vypnuto u {(s.mista || []).filter(m => !m.prirazena).length}</span>
                                    )}
                                </span>
                            </button>
                            <button type="button" onClick={() => setEditor(s.id)} title="Upravit název, položky a ceny"
                                className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg flex-shrink-0"
                                style={{ background: `${BARVA}14`, border: `1px solid ${BARVA}55`, cursor: 'pointer', color: BARVA }}>
                                <Pencil size={12} /> Upravit
                            </button>
                            <button type="button" onClick={() => smazat(s)} title="Smazat"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}><Trash2 size={14} /></button>
                        </div>
                    ))}
                </div>
            </ModalShell>

            {editor !== undefined && (
                <ComboSkupinaEditor skupinaId={editor} isDark={isDark}
                    onZavrit={() => { setEditor(undefined); nacist(); }} />
            )}
        </>
    );
}
